import { useQuery, useQueryClient, useMutation, UseQueryResult, UseMutationResult } from '@tanstack/react-query';
import {
  fetchAllProjects,
  createProjectRequest,
  updateProjectRequest,
  requestProjectReadme,
  updateProjectReadmeRequest,
  requestProjectReadmeVersions,
  requestProjectHistory,
  fetchProjectSettings,
  saveProjectSettings,
  fetchProjectQaQueue,
  fetchProjectFixQueue,
  fetchProjectMyQA,
  fetchProjectMyFixing,
} from '_/api/projects/project.api';
import { readmeHistoryAdapter } from '_/common/adapters/readmeHistory.adapter';
import { IHistoryItem } from '_/common/history/history.types';
import { IProject } from '_/types/projects/project.types';
import { notification } from 'antd';

export const PROJECTS_QUERY_KEY = (orgSlug: string): string[] => ['projects', orgSlug];
export const PROJECT_QUERY_KEY = (projectSlug: string): string[] => ['project', projectSlug];
export const PROJECT_README_QUERY_KEY = (projectId: string): string[] => ['project', projectId, 'readme'];
export const PROJECT_README_VERSIONS_QUERY_KEY = (projectId: string): string[] => ['project', projectId, 'readmeVersions'];
export const PROJECT_HISTORY_QUERY_KEY = (projectId: string): string[] => ['project', projectId, 'history'];
export const PROJECT_SETTINGS_QUERY_KEY = (projectId: string): string[] => ['project', projectId, 'settings'];
export const PROJECT_QA_QUEUE_QUERY_KEY = (projectId: string): string[] => ['project', projectId, 'qaqueue'];
export const PROJECT_FIX_QUEUE_QUERY_KEY = (projectId: string): string[] => ['project', projectId, 'fixqueue'];
export const PROJECT_MY_QA_QUERY_KEY = (projectId: string): string[] => ['project', projectId, 'myqa'];
export const PROJECT_MY_FIXING_QUERY_KEY = (projectId: string): string[] => ['project', projectId, 'myfixing'];

export const useProjects = (orgSlug?: string | null): UseQueryResult<any> => {
  return useQuery({
    queryKey: PROJECTS_QUERY_KEY(orgSlug || ''),
    queryFn: fetchAllProjects,
    enabled: !!orgSlug,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to create a project
 */
export const useCreateProject = (): UseMutationResult<any, Error, { project: Partial<IProject>; projectReadme: string }> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ project, projectReadme }: { project: Partial<IProject>; projectReadme: string }) =>
      createProjectRequest(project, projectReadme),
    onSuccess: () => {
      // Invalidate all projects queries to refresh lists
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
};

/**
 * Hook to fetch project readme
 */
export const useProjectReadme = (projectId?: string): UseQueryResult<any> => {
  return useQuery({
    queryKey: PROJECT_README_QUERY_KEY(projectId || ''),
    queryFn: () => requestProjectReadme(projectId!),
    enabled: !!projectId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Hook to fetch project readme versions
 */
export const useProjectReadmeVersions = (projectId?: string): UseQueryResult<any> => {
  return useQuery({
    queryKey: PROJECT_README_VERSIONS_QUERY_KEY(projectId || ''),
    queryFn: async () => {
      const versions = await requestProjectReadmeVersions(projectId!);
      // Apply adapter to transform versions
      const transformedVersions = readmeHistoryAdapter(versions);
      return {
        items: transformedVersions,
        cursor: undefined,
      };
    },
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to fetch project history
 */
export const useProjectHistory = (projectId?: string): UseQueryResult<{ items: IHistoryItem<IProject>[]; cursor?: string }> => {
  return useQuery({
    queryKey: PROJECT_HISTORY_QUERY_KEY(projectId || ''),
    queryFn: async () => {
      const response = await requestProjectHistory(projectId!);
      return {
        items: response?.items || [],
        cursor: response?.metadata.cursor,
      };
    },
    enabled: !!projectId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Hook to fetch more project history (pagination)
 */
export const useFetchMoreProjectHistory = (projectId?: string): UseMutationResult<any, Error, string> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (cursor: string) => {
      const response = await requestProjectHistory(projectId!, {
        queryParameters: { cursor: cursor },
      });
      return {
        items: response?.items || [],
        cursor: response?.metadata?.cursor,
      };
    },
    onSuccess: (newData) => {
      // Append new data to existing data
      queryClient.setQueryData(PROJECT_HISTORY_QUERY_KEY(projectId!), (oldData: any) => {
        if (!oldData) return newData;
        return {
          items: [...oldData.items, ...newData.items],
          cursor: newData.cursor,
        };
      });
    },
  });
};

/**
 * Hook to update a project
 */
export const useUpdateProject = (
  projectSlug?: string,
): UseMutationResult<any, Error, { projectId: string; project: Partial<IProject> }> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ projectId, project }: { projectId: string; project: Partial<IProject> }) =>
      updateProjectRequest(projectId, project),
    onSuccess: (_, variables) => {
      const { projectId } = variables;
      // Invalidate the specific project query
      if (projectSlug) {
        void queryClient.invalidateQueries({ queryKey: PROJECT_QUERY_KEY(projectSlug) });
      }
      // Invalidate all projects queries to refresh lists
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
      // Invalidate project history to refresh activity
      if (projectId) {
        void queryClient.invalidateQueries({ queryKey: PROJECT_HISTORY_QUERY_KEY(projectId) });
      }
      notification.success({
        message: 'Project Updated Successfully',
      });
    },
    onError: (error: any) => {
      console.error('Error updating project', error);
      notification.error({
        message: error.message || 'Error updating project',
      });
    },
  });
};

/**
 * Hook to update project readme
 */
export const useUpdateProjectReadme = (projectSlug?: string, projectId?: string): UseMutationResult<any, Error, string> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (content: string) => updateProjectReadmeRequest(projectId!, content),
    onSuccess: () => {
      // Invalidate project readme query
      if (projectId) {
        void queryClient.invalidateQueries({ queryKey: PROJECT_README_QUERY_KEY(projectId) });
        void queryClient.invalidateQueries({ queryKey: PROJECT_README_VERSIONS_QUERY_KEY(projectId) });
        // Invalidate project history to refresh activity
        void queryClient.invalidateQueries({ queryKey: PROJECT_HISTORY_QUERY_KEY(projectId) });
      }
      // Invalidate the specific project query
      if (projectSlug) {
        void queryClient.invalidateQueries({ queryKey: PROJECT_QUERY_KEY(projectSlug) });
      }
      notification.success({
        message: 'Project Readme Saved Successfully',
      });
    },
    onError: (error: any) => {
      console.error('Error saving project readme', error);
      notification.error({
        message: error.message || 'Error saving project readme',
      });
    },
  });
};

/**
 * Hook to fetch project settings
 */
export const useProjectSettings = (projectId?: string): UseQueryResult<any> => {
  return useQuery({
    queryKey: PROJECT_SETTINGS_QUERY_KEY(projectId || ''),
    queryFn: () => fetchProjectSettings(projectId!),
    enabled: !!projectId,
    staleTime: 60 * 1000, // 1 minute - settings don't change frequently
  });
};

/**
 * Hook to save project settings
 */
export const useSaveProjectSettings = (projectId: string): UseMutationResult<any, Error, { settings: any; isNew: boolean }> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ settings, isNew }: { settings: any; isNew: boolean }) => saveProjectSettings(projectId, settings, isNew),
    onSuccess: (data) => {
      // Update the cache with the returned settings
      queryClient.setQueryData(PROJECT_SETTINGS_QUERY_KEY(projectId), data);
      // Invalidate project history to refresh activity
      void queryClient.invalidateQueries({ queryKey: PROJECT_HISTORY_QUERY_KEY(projectId) });
      notification.success({
        message: 'Project Settings Saved',
      });
    },
    onError: (error: any) => {
      console.error('Error saving project settings', error);
      notification.error({
        message: error.message || 'Error saving project settings',
      });
      throw error;
    },
  });
};

export const useProjectQaQueue = (projectId?: string): UseQueryResult<any[]> => {
  return useQuery({
    queryKey: PROJECT_QA_QUEUE_QUERY_KEY(projectId || ''),
    queryFn: () => fetchProjectQaQueue(projectId!),
    enabled: !!projectId,
    staleTime: 30 * 1000,
  });
};

export const useProjectFixQueue = (projectId?: string): UseQueryResult<any[]> => {
  return useQuery({
    queryKey: PROJECT_FIX_QUEUE_QUERY_KEY(projectId || ''),
    queryFn: () => fetchProjectFixQueue(projectId!),
    enabled: !!projectId,
    staleTime: 30 * 1000,
  });
};

export const useProjectMyQA = (projectId?: string): UseQueryResult<any[]> => {
  return useQuery({
    queryKey: PROJECT_MY_QA_QUERY_KEY(projectId || ''),
    queryFn: () => fetchProjectMyQA(projectId!),
    enabled: !!projectId,
    staleTime: 30 * 1000,
  });
};

export const useProjectMyFixing = (projectId?: string): UseQueryResult<any[]> => {
  return useQuery({
    queryKey: PROJECT_MY_FIXING_QUERY_KEY(projectId || ''),
    queryFn: () => fetchProjectMyFixing(projectId!),
    enabled: !!projectId,
    staleTime: 30 * 1000,
  });
};
